// @ts-nocheck
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '@way/theme-host/services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { PackageService } from '@way/theme-host/services/package.service';
import { Package } from '@way/theme-host/shared/types';
import {
  DarkPageHeaderComponent as PageHeaderComponent,
  NavigationItem,
  Logo,
  CTAButton,
} from '../shared/page-header/page-header.component';
import { DarkPageFooterComponent as PageFooterComponent } from '../shared/footer/footer.component';
import { LocationService } from '@way/theme-host/services/location.service';
import { environment } from '@way/theme-host/env/environment';
import { getReferrerUrl } from '@way/theme-host/shared/utils/website-url.util';
import { NotificationService } from '../../../../app/services/notification.service'; // NotificationService;
import { resolveFooterQuickLinks } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface WebsiteElement {
  id: string;
  type:
    | 'header'
    | 'hero'
    | 'section'
    | 'testimonials'
    | 'services'
    | 'booking'
    | 'contact'
    | 'footer'
    | 'fleets'
    | 'packages'
    | 'coupons'
    | 'express-details';
  content: any;
  selected?: boolean;
}

interface FleetFormData {
  company_name: string;
  contact_name: string;
  phone: string;
  email: string;
  wash_type: string;
  vehicle_count: number | null;
  location: string;
  notes: string;
}

@Component({
  selector: 'app-dark-fleet-management-preview',
  standalone: true,
  providers: [NotificationService],
  imports: [CommonModule, FormsModule, PageHeaderComponent, PageFooterComponent],
  templateUrl: './fleet-management-preview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./fleet-management-preview.component.scss'],
})
export class DarkFleetManagementPreviewComponent implements OnInit, OnChanges {
  @Input() pageData: any = null;
  @Input() sectionVisibility: Record<string, boolean> = {};
  @Input() pages: any[] = [];
  @Input() currentPageType = '';
  @Input() elements: WebsiteElement[] = [];
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Input() primaryColor: string | null = null; // Primary color from theme
  @Input() headingColor: string | null = null; // Heading color from branding
  @Input() paragraphColor: string | null = null; // Paragraph/description color from branding
  @Input() headingFont: string | null = null; // Heading font from branding
  @Input() bodyFont: string | null = null; // Body font from branding
  @Output() elementSelect = new EventEmitter<WebsiteElement>();
  @Output() navigationClick = new EventEmitter<string>();

  packages: Package[] = [];
  loading = false;
  submitting = false;

  // Form data
  formData: FleetFormData = {
    company_name: '',
    contact_name: '',
    phone: '',
    email: '',
    wash_type: '',
    vehicle_count: null,
    location: '',
    notes: '',
  };

  // Locations
  locations: any[] = [];
  isLoadingLocations = false;
  locationsError = '';

  // Page data from JSON
  headerData: any = null;
  pageTitleData: any = null;
  formSectionData: any = null;
  footerData: any = null;

  constructor(
    private logger: LoggerService,
    private packageService: PackageService,
    private http: HttpClient,
    private locationService: LocationService,
    private messageService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadPageContent();
    this.loadPackages();
    this.loadLocations();
  }

  /**
   * Handle Angular OnChanges lifecycle to reload page content, update header navigation, and refresh section data when relevant inputs change.
   * @example
   * gOnChanges({ pageData: { firstChange: false }, elements: { firstChange: false } })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - The SimpleChanges object containing changed input properties.
   * @returns {{void}} Nothing is returned.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageData'] && !changes['pageData'].firstChange) {
      this.loadPageContent();
    }
    // Update navigation when pages or currentPageType changes
    if ((changes['pages'] || changes['currentPageType']) && this.headerData) {
      this.updateHeaderNavigation();
    }
    // Reload page content when elements change (for background color updates)
    if (changes['elements'] && !changes['elements'].firstChange) {
      // Force reload of pageTitleData and formSectionData to get updated background color
      const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
      const sections = page?.sections || {};
      this.pageTitleData = sections['page-title'] || {};
      this.formSectionData = sections.form_section || {};
    }
  }

  /**
   * Load page content from the component's pageData and populate header, page-title, form_section, and footer section state.
   * @example
   * this.loadPageContent()
   * undefined
   * @returns {void} No return value; updates component properties (headerData, pageTitleData, formSectionData, footerData) and refreshes header navigation.
   **/
  private loadPageContent(): void {
    if (!this.pageData) return;

    this.logger.debug('?? Loading Fleet Management page content:', this.pageData);

    // Extract sections from page data - handle both editor and preview formats
    // Editor format: pageData.page.sections
    // Preview format: pageData.json_data.page.sections
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    const sections = page?.sections || {};

    this.headerData = sections.header || {};
    this.pageTitleData = sections['page-title'] || {};
    this.formSectionData = sections.form_section || {};
    this.footerData = sections.footer || {};

    this.logger.debug('Fleet page sections loaded:', {
      header: this.headerData,
      hero: this.pageTitleData,
      form: this.formSectionData,
      footer: this.footerData,
    });

    // Update header navigation to match current pages
    this.updateHeaderNavigation();
  }

  /**
   * Update the component's headerData.navigation from pages (filtering out invisible pages) and ensure a default CTA button exists.
   * @example
   * updateHeaderNavigation()
   * undefined
   * @returns {void} Updates this.headerData in place; does not return a value.
   */
  private updateHeaderNavigation(): void {
    if (!this.headerData) this.headerData = {};

    // Generate navigation from pages if pages are provided
    // Filter out inactive pages (visible: false means active: false)
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      this.headerData.navigation = activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
      this.logger.debug(
        'Updated header navigation for Fleet Management:',
        this.headerData.navigation
      );
    }

    // Ensure CTA button exists
    if (!this.headerData.ctaButton && !this.headerData.button) {
      this.headerData.ctaButton = {
        text: '',
        link: '#booking',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }
  }

  /**
   * Loads car wash packages from the packageService and updates the component state (packages and loading indicator).
   * @example
   * this.loadPackages()
   * void
   * @returns {{void}} No return value; updates component properties and handles loading/error states.
   **/
  private loadPackages(): void {
    this.loading = true;

    this.packageService.getCarWashPackages().subscribe({
      next: response => {
        this.logger.debug('? Packages loaded for fleet form:', response);
        if (response.status && response.data) {
          this.packages = response.data;
        }
        this.loading = false;
      },
      error: err => {
        this.logger.error('? Error loading packages:', err);
        this.loading = false;
      },
    });
  }

  /**
   * Check if we're in website preview mode (URL contains /preview?websiteId=)
   */
  private isWebsitePreview(): boolean {
    return (
      window.location.href.includes('/preview?websiteId=') ||
      window.location.href.includes('/preview?websiteld=')
    );
  }

  private isLocalhostOrPreview(): boolean {
    const hostname = window.location.hostname;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0' ||
      hostname.startsWith('192.168.') ||
      hostname.startsWith('10.') ||
      hostname.startsWith('172.') ||
      window.location.href.includes('preview') ||
      window.location.href.includes('localhost')
    );
  }

  /**
   * Check if we're in admin/preview mode (has token in localStorage)
   */
  private isAdminMode(): boolean {
    try {
      const userData = localStorage.getItem('userData');
      const token = localStorage.getItem('token');
      return !!(userData || token);
    } catch {
      return false;
    }
  }

  /**
   * Load and populate the component's locations if they are not already loaded.
   * This method guards against duplicate loads, toggles loading state, and:
   * - If running in admin mode, calls locationService.getLocations(), validates and normalizes the response,
   *   filters active locations, maps them into the component's locations array, and auto-selects the first location
   *   into formData.location if not already set.
   * - Handles invalid responses and errors by clearing locations and setting a user-facing locationsError.
   * - If not in admin mode (preview/non-authenticated), it clears locations and sets locationsError prompting login.
   * @example
   * oadLocations()
   * void
   * @returns {void} Does not return a value; updates component state (locations, isLoadingLocations, locationsError, formData.location).
   */
  loadLocations(): void {
    if (this.locations.length > 0) {
      return; // Already loaded
    }

    if (this.isLoadingLocations) {
      return; // Already loading
    }

    this.isLoadingLocations = true;
    this.locationsError = '';

    // Check if in admin mode (has token) - use LocationService which handles auth
    if (this.isAdminMode()) {
      this.logger.debug('📍 Loading locations using LocationService (admin mode)');
      this.locationService.getLocations().subscribe({
        next: response => {
          this.isLoadingLocations = false;
          if (response && response.status && response.data && Array.isArray(response.data)) {
            this.locations = response.data
              .filter((location: any) => {
                return (
                  location &&
                  typeof location === 'object' &&
                  (location.status === true || location.status === 'active')
                );
              })
              .map((location: any) => {
                return {
                  id: location?.id || '',
                  name: location?.branch || location?.name || 'Unknown Location',
                  address:
                    `${location?.street_address || ''}, ${location?.city || ''}, ${location?.state || ''} ${location?.zip_code || ''}`.trim(),
                  fullData: location,
                };
              });

            if (this.locations.length === 0) {
              this.locationsError = 'No locations found.';
            } else {
              // Auto-select first location if available
              if (!this.formData.location && this.locations[0]?.id) {
                this.formData.location = this.locations[0].id;
              }
            }
          } else {
            this.logger.warn('Invalid location response format or no locations:', response);
            this.locations = [];
            this.locationsError = 'No locations found.';
          }
        },
        error: error => {
          this.logger.error('Error loading locations via LocationService:', error);
          this.isLoadingLocations = false;
          this.locations = [];
          this.locationsError = 'No locations found. Please try again later.';
        },
      });
      return;
    }

    // Not in admin mode - for preview components, we require authentication
    this.logger.debug('📍 Not in admin mode - locations require authentication');
    this.isLoadingLocations = false;
    this.locations = [];
    this.locationsError = 'Please log in to load locations.';
  }

  /**
   * Submits fleet form data to the backend after validating required fields, handling website preview and localhost modes, attaching an optional auth token and headers, and resetting the form on success.
   * @example
   * nSubmit()
   * undefined
   * @returns {{void}} Void - Performs network request and UI side-effects (alerts, console logs, form reset) and does not return a value.
   **/
  onSubmit(): void {
    // Check if we're in website preview mode first
    if (this.isWebsitePreview()) {
      this.messageService.add({
        severity: 'info',
        summary: 'Preview Mode',
        detail: 'Form submission is only available on the published website.',
      });
      return;
    }

    // Validate required fields
    if (
      !this.formData.company_name ||
      !this.formData.contact_name ||
      !this.formData.phone ||
      !this.formData.email ||
      !this.formData.wash_type ||
      !this.formData.vehicle_count
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields.',
      });
      return;
    }

    const locationId = this.formData.location || this.locations[0]?.id;
    if (!locationId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'No location is available for this submission.',
      });
      return;
    }

    // Find the selected package to get its ID
    const selectedPackage = this.packages.find(pkg => pkg.package_name === this.formData.wash_type);
    if (!selectedPackage || !selectedPackage.id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please select a valid wash type.',
      });
      return;
    }

    // Check if we're in localhost or preview environment
    if (this.isLocalhostOrPreview()) {
      this.messageService.add({
        severity: 'success',
        summary: 'Demo Mode',
        detail: 'Form submitted successfully (demo mode).',
      });
      // Reset form
      this.formData = {
        company_name: '',
        contact_name: '',
        phone: '',
        email: '',
        wash_type: '',
        vehicle_count: null,
        location: '',
        notes: '',
      };
      return;
    }

    this.submitting = true;

    // Get referrer URL dynamically
    const referrer = getReferrerUrl();

    // Get token from localStorage if available (for admin/preview mode)
    let authToken: string | null = null;
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsedData = JSON.parse(userData);
        authToken = parsedData?.token || null;
      }
    } catch (error) {
      this.logger.error('Error parsing userData:', error);
    }

    // Prepare request headers
    let headers = new HttpHeaders({
      Referer: referrer,
      'Content-Type': 'application/json',
    });

    // Add Authorization header if token is available
    if (authToken) {
      headers = headers.set('Authorization', `Bearer ${authToken}`);
    }

    // Prepare request body matching expected API structure
    const requestBody = {
      name: this.formData.company_name,
      customer_name: this.formData.contact_name,
      contact: this.formData.phone,
      email: this.formData.email,
      package_id: selectedPackage.id,
      location_id: locationId,
      vehicle_count: this.formData.vehicle_count,
      special_notes: this.formData.notes || '',
    };

    // Construct API URL from environment baseUrl
    const apiUrl = `${environment.apiUrl}/chat/fleet`;

    // Make API call
    this.http.post<any>(apiUrl, requestBody, { headers }).subscribe({
      next: response => {
        this.submitting = false;
        this.logger.debug('Fleet created successfully:', response);

        if (response.status && response.status_code === 200) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Fleet created successfully!',
          });
          this.formData = {
            company_name: '',
            contact_name: '',
            phone: '',
            email: '',
            wash_type: '',
            vehicle_count: null,
            location: '',
            notes: '',
          };
        } else {
          if (response.message && response.message.includes('API key not found')) {
            this.messageService.add({
              severity: 'error',
              summary: 'Configuration Error',
              detail: 'This domain is not configured for API access. Please contact support.',
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.message || 'Failed to create fleet. Please try again.',
            });
          }
        }
      },
      error: error => {
        this.submitting = false;
        this.logger.error('Error creating fleet:', error);

        // Check for API key error in error response
        const errorResponse = error.error;
        if (
          errorResponse &&
          errorResponse.message &&
          (errorResponse.message.includes('API key not found') ||
            errorResponse.message.includes('API key not found for domain'))
        ) {
          this.messageService.add({
            severity: 'error',
            summary: 'Configuration Error',
            detail: 'This domain is not configured for API access. Please contact support.',
          });
        } else {
          const errorMessage =
            errorResponse?.message || error.message || 'Failed to create fleet. Please try again.';
          this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMessage });
        }
      },
    });
  }

  /**
   * Check if a section should be visible based on section visibility settings
   */
  isSectionVisible(sectionKey: string): boolean {
    if (!this.sectionVisibility || Object.keys(this.sectionVisibility).length === 0) {
      return true;
    }
    return this.sectionVisibility[sectionKey] !== false;
  }

  // Header data helpers for shared component
  /**
   * Returns the header logo information (image, text, link) extracted from the component's header element, using sensible defaults when values are missing.
   * @example
   * etHeaderLogo()
   * { image: '', text: 'Logo', link: '/' }
   * @returns {{image: string, text: string, link: string}} Return header logo object with image, text and link; defaults to text 'Logo' and link '/' when not provided.
   */
  getHeaderLogo(): Logo {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.logo) {
      return {
        image: headerContent.logo.image || headerContent.logo.url || '',
        text: headerContent.logo.text || 'Logo',
        link: headerContent.logo.link || '/',
      };
    }
    return { text: 'Logo', link: '/' };
  }

  /**
   * Returns header navigation items built from the component's header content or fallback pages.
   * @example
   * etHeaderNavigation()
   * [
   *   { label: "Dashboard", link: "#dashboard", active: false },
   *   { label: "Fleet Management", link: "#fleet-management", active: true }
   * ]
   * @returns {{NavigationItem[]}} Array of navigation items for the header.
   **/
  getHeaderNavigation(): NavigationItem[] {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.navigation && Array.isArray(headerContent.navigation)) {
      return headerContent.navigation;
    }
    if (this.pages && this.pages.length > 0) {
      const activePages = this.pages.filter((page: any) => page.visible !== false);
      return activePages.map((page: any) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        active: page.pageType === this.currentPageType,
      }));
    }
    return [];
  }

  /**
   * Retrieve the header CTA button configuration from the component's header content, falling back to default values when not provided.
   * @example
   * etHeaderCTAButton()
   * { text: 'Book Wash', link: '#', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' }
   * @returns {{CTAButton | null}} CTAButton object with text, link, backgroundColor and textColor, or null.
   */
  getHeaderCTAButton(): CTAButton | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.ctaButton) {
      return {
        text: headerContent.ctaButton.text || headerContent.ctaButton.label || 'Book Wash',
        link: headerContent.ctaButton.link || '#',
        backgroundColor: headerContent.ctaButton.backgroundColor || '#1B5BFF',
        textColor: headerContent.ctaButton.textColor || '#FFFFFF',
      };
    }
    return {
      text: 'Book Wash',
      link: '#',
      backgroundColor: '#1B5BFF',
      textColor: '#FFFFFF',
    };
  }

  getHeaderBackgroundColor(): string | null {
    const headerElement = this.getHeaderElement();
    const headerContent = headerElement?.content as any;
    if (headerContent?.backgroundColor) {
      return headerContent.backgroundColor as string;
    }
    return null;
  }

  /**
   * Returns an array of footer quick links. Prefers footer content quickLinks if present; otherwise derives links from visible pages.
   * @example
   * etFooterQuickLinks()
   * [{ label: 'Home', link: '#home' }, { label: 'About Us', link: '#about-us' }]
   * @returns {{Array<{ label: string; link: string }>} } Array of quick link objects with label and link.
   **/
  getFooterQuickLinks(): { label: string; link: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    const storedLinks = Array.isArray(footerContent?.quickLinks) ? footerContent.quickLinks : [];
    return resolveFooterQuickLinks(storedLinks, this.pages);
  }

  /**
   * Retrieve normalized social link objects from the footer content.
   * @example
   * etFooterSocialLinks()
   * [{ platform: 'Twitter', url: 'https://twitter.com/example', icon: 'twitter' }]
   * @returns {{Array<{ platform: string; url: string; icon: string }>}} Array of social link objects with platform, url and icon properties.
   **/
  getFooterSocialLinks(): { platform: string; url: string; icon: string }[] {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.socialLinks && Array.isArray(footerContent.socialLinks)) {
      return footerContent.socialLinks.map((social: any) => ({
        platform: social.platform || '',
        url: social.url || social.link || '#',
        icon: social.icon || social.platform?.toLowerCase() || 'link',
      }));
    }
    return [];
  }

  getFooterSocialLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.socialLinksTitle || 'Connect with us';
  }

  getFooterQuickLinksTitle(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    return footerContent?.quickLinksTitle || 'Quick links';
  }

  getFooterCopyright(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.copyright) {
      return footerContent.copyright;
    }
    return `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
  }

  /**
   * Retrieve the footer logo data from the component, falling back to the header logo or a default.
   * @example
   * etFooterLogo()
   * { text: 'way' }
   * @returns {{ text?: string; image?: string } | undefined} The footer logo object containing optional text and image, or undefined if none is available.
   **/
  getFooterLogo(): { text?: string; image?: string } | undefined {
    // Get logo from footer element content
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;

    if (footerContent?.logo) {
      const logo = footerContent.logo;
      return {
        text: logo.text || 'way',
        image: logo.image || logo.url || '',
      };
    }

    // Fallback to header logo if footer logo not available
    const headerLogo = this.getHeaderLogo();
    if (headerLogo && headerLogo.image) {
      return {
        text: headerLogo.text,
        image: headerLogo.image,
      };
    }
    return { text: 'way' };
  }

  getFooterBackgroundColor(): string {
    const footerElement = this.getFooterElement();
    const footerContent = footerElement?.content as any;
    if (footerContent?.backgroundColor) {
      return footerContent.backgroundColor;
    }
    return '#1B1B1B';
  }

  getHeaderElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'header');
  }

  getFooterElement(): WebsiteElement | undefined {
    return this.elements?.find(e => e.id === 'footer');
  }

  onHeaderClick(): void {
    const headerElement = this.getHeaderElement();
    if (headerElement) {
      this.onElementClick(headerElement);
    }
  }

  onElementClick(element: WebsiteElement): void {
    if (!element) return;
    this.elementSelect.emit(element);
  }

  onNavigationClick(pageType: string): void {
    this.navigationClick.emit(pageType);
  }

  /**
   * Get primary color from theme or use default
   */
  getPrimaryColor(): string {
    return this.primaryColor || '#1B5BFF';
  }

  /**
   * Get heading color from branding or use default
   */
  getHeadingColor(): string {
    return this.headingColor || '#FFFFFF';
  }

  /**
   * Get paragraph color from branding or use default
   */
  getParagraphColor(): string {
    return this.paragraphColor || '#D3D5D9';
  }

  /**
   * Get heading font from branding or use default
   */
  getHeadingFont(): string {
    return this.headingFont || 'Plus Jakarta Sans';
  }

  /**
   * Get body font from branding or use default
   */
  getBodyFont(): string {
    return this.bodyFont || 'Inter';
  }

  /**
   * Get hero title text
   */
  getPageTitle(): string {
    if (this.pageTitleData?.title) {
      if (typeof this.pageTitleData.title === 'string') {
        return this.pageTitleData.title;
      } else if (this.pageTitleData.title?.label) {
        return this.pageTitleData.title.label;
      }
    }
    return 'Fleet Management';
  }

  /**
   * Get hero subtitle/description text
   */
  getPageSubtitle(): string {
    if (this.pageTitleData?.description) {
      return this.pageTitleData.description;
    } else if (this.pageTitleData?.subtitle) {
      return this.pageTitleData.subtitle;
    }
    return 'Keep your business vehicles spotless and road-ready with our tailored fleet wash plans. Enjoy convenient scheduling, consistent quality, and cost savings designed for companies of any size.';
  }

  /**
   * Get title styles for ngStyle binding
   */
  getTitleStyles(): any {
    // Check page-level title first (highest priority)
    const page = this.pageData?.page || this.pageData;
    if (page?.title && typeof page.title === 'object' && page.title.style?.text_style) {
      const titleStyleObj = page.title.style.text_style;
      const styles: any = {};
      if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
      if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
      if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
      if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
      return styles;
    }

    // Fallback to section-level title
    if (this.pageTitleData?.title && typeof this.pageTitleData.title === 'object') {
      const title = this.pageTitleData.title;
      const titleStyleObj = title.style?.text_style;

      if (titleStyleObj && typeof titleStyleObj === 'object') {
        const styles: any = {};
        if (titleStyleObj.fontWeight) styles['font-weight'] = titleStyleObj.fontWeight;
        if (titleStyleObj.fontStyle) styles['font-style'] = titleStyleObj.fontStyle;
        if (titleStyleObj.textDecoration) styles['text-decoration'] = titleStyleObj.textDecoration;
        if (titleStyleObj.textAlign) styles['text-align'] = titleStyleObj.textAlign;
        return styles;
      }
    }

    return {};
  }

  /**
   * Get form theme
   */
  getFormTheme(): 'light' | 'dark' {
    return this.formSectionData?.theme || this.formSectionData?.formTheme || 'light';
  }

  /**
   * Get button text
   */
  getButtonText(): string {
    return this.formSectionData?.buttonText || this.formSectionData?.button?.text || 'Submit';
  }

  /**
   * Get fleet section element for selection
   */
  getFleetSectionElement(): WebsiteElement | undefined {
    return this.elements.find(
      el =>
        el.type === 'fleets' ||
        el.id === 'fleets' ||
        (el as any).originalKey === 'page-title' ||
        (el as any).originalKey === 'form_section'
    );
  }

  /**
   * Get page header section background color
   */
  getPageHeaderSectionBackgroundColor(): string | null {
    // Priority 1: Check element content (most up-to-date, updated immediately)
    const fleetElement = this.getFleetSectionElement();
    const fleetContent = fleetElement?.content as any;
    if (fleetContent?.backgroundColor) {
      return fleetContent.backgroundColor;
    }
    // Priority 2: Check pageTitleData (from API/pageData)
    if (this.pageTitleData?.backgroundColor) {
      return this.pageTitleData.backgroundColor;
    }
    // Priority 3: Check formSectionData (from API/pageData)
    if (this.formSectionData?.backgroundColor) {
      return this.formSectionData.backgroundColor;
    }
    // Priority 4: Check page-level theme background color
    const page = this.pageData?.page || this.pageData?.json_data?.page || this.pageData;
    if (page?.theme?.background_color) {
      return page.theme.background_color;
    }
    // Priority 5: Check pageData directly
    const sections = page?.sections || {};
    const pageTitleSection = sections['page-title'] || {};
    if (pageTitleSection.backgroundColor) {
      return pageTitleSection.backgroundColor;
    }
    const formSection = sections.form_section || {};
    if (formSection.backgroundColor) {
      return formSection.backgroundColor;
    }
    // Default fallback
    return null;
  }

  /**
   * Get form section background color (same as page header for unified look)
   */
  getFormSectionBackgroundColor(): string | null {
    return this.getPageHeaderSectionBackgroundColor();
  }

  /**
   * Handle hero section click
   */
  onPageHeaderSectionClick(event: Event): void {
    event.stopPropagation();
    const fleetElement: WebsiteElement = {
      id: 'fleets',
      type: 'fleets',
      content: {
        ...(this.pageTitleData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        formTheme: this.getFormTheme(),
        buttonText: this.getButtonText(),
        backgroundColor: this.getPageHeaderSectionBackgroundColor(),
      },
      selected: true,
      originalKey: 'page-title',
    } as any;
    this.logger.debug('🚛 Hero section clicked, emitting element:', fleetElement);
    this.elementSelect.emit(fleetElement);
  }

  /**
   * Handle form section click
   */
  onFormSectionClick(event: Event): void {
    event.stopPropagation();
    const fleetElement: WebsiteElement = {
      id: 'fleets',
      type: 'fleets',
      content: {
        ...(this.pageTitleData || {}),
        title: this.getPageTitle(),
        subtitle: this.getPageSubtitle(),
        formTheme: this.getFormTheme(),
        buttonText: this.getButtonText(),
        backgroundColor: this.getFormSectionBackgroundColor(),
      },
      selected: true,
      originalKey: 'form_section',
    } as any;
    this.logger.debug('🚛 Form section clicked, emitting element:', fleetElement);
    this.elementSelect.emit(fleetElement);
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
